import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from './SEO';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { useAdRedirect, AdToast } from '../hooks/useAdRedirect';
import { AdUnit } from './AdUnit';
import { 
  Hash, 
  Copy, 
  Check, 
  ArrowLeft, 
  RotateCcw, 
  Share2, 
  Search, 
  CheckCircle2, 
  TrendingUp, 
  Plus 
} from 'lucide-react';

export const HashtagGenerator = () => {
  const { withAd, toast } = useAdRedirect();
  const [topic, setTopic] = useState('digital marketing');
  const [platform, setPlatform] = useState('instagram'); // 'instagram' | 'tiktok' | 'linkedin' | 'youtube'
  const [activeHashtags, setActiveHashtags] = useState([
    '#digitalmarketing', '#marketing', '#socialmediamarketing', '#socialmedia',
    '#business', '#seo', '#branding', '#marketingdigital', '#onlinemarketing',
    '#entrepreneur', '#contentmarketing', '#marketingstrategy', '#digitalmarketingagency',
    '#digital', '#marketingtips', '#smallbusiness', '#webdesign', '#graphicdesign',
    '#b2bmarketing', '#growthhacking', '#searchengineoptimization', '#advertising',
    '#socialmediatips', '#businesstips', '#marketingonline', '#digitalstrategy',
    '#contentcreator', '#brandstrategy', '#marketinglife', '#socialmediamanager'
  ]);
  const [copied, setCopied] = useState(false);

  const hashtagPool = {
    'digital marketing': [
      '#digitalmarketing', '#marketing', '#socialmediamarketing', '#socialmedia',
      '#business', '#seo', '#branding', '#marketingdigital', '#onlinemarketing',
      '#entrepreneur', '#contentmarketing', '#marketingstrategy', '#digitalmarketingagency',
      '#digital', '#marketingtips', '#smallbusiness', '#webdesign', '#graphicdesign',
      '#b2bmarketing', '#growthhacking', '#searchengineoptimization', '#advertising'
    ],
    'travel vlog': [
      '#travel', '#travelphotography', '#travelgram', '#wanderlust', '#instatravel',
      '#nature', '#traveling', '#photooftheday', '#vacation', '#adventure', '#travelblogger',
      '#explore', '#trip', '#beautifuldestinations', '#tourism', '#solotravel', '#mountains'
    ],
    'tech gadget': [
      '#tech', '#technology', '#gadgets', '#electronics', '#smartphone', '#innovation',
      '#instatech', '#techtrends', '#futuretech', '#appletech', '#android', '#smarthome',
      '#gaming', '#techlover', '#unboxing', '#pcsetup', '#technews', '#gadgetlover'
    ],
    'healthy food': [
      '#healthyfood', '#healthylifestyle', '#food', '#foodporn', '#instafood',
      '#foodie', '#fitness', '#vegan', '#healthyeating', '#cleaneating', '#diet',
      '#recipe', '#nutrition', '#superfood', '#glutenfree', '#salad', '#fitfood'
    ]
  };

  const generateHashtags = (e) => {
    if (e) e.preventDefault();
    const query = topic.toLowerCase().trim();
    if (!query) return;

    if (hashtagPool[query]) {
      setActiveHashtags(hashtagPool[query]);
    } else {
      const baseWords = query.split(' ');
      const synthesized = [
        `#${query.replace(/\s+/g, '')}`,
        `#${query.replace(/\s+/g, '')}tips`,
        `#${query.replace(/\s+/g, '')}strategy`,
        `#${query.replace(/\s+/g, '')}life`,
        `#${baseWords[0]}`,
        `#${baseWords[0]}lover`,
        `#${baseWords[0]}world`,
        `#${baseWords[0]}addict`,
        `#viral${baseWords[0]}`,
        `#trending${baseWords[0]}`,
        `#${query.replace(/\s+/g, '')}community`,
        `#${query.replace(/\s+/g, '')}goals`,
        `#${query.replace(/\s+/g, '')}2026`,
        `#explorepage`,
        `#fyp`
      ];
      setActiveHashtags(synthesized);
    }
  };

  const toggleHashtag = (tag) => {
    if (activeHashtags.includes(tag)) {
      setActiveHashtags(activeHashtags.filter(t => t !== tag));
    } else {
      setActiveHashtags([...activeHashtags, tag]);
    }
  };

  const hashtagsString = activeHashtags.join(' ');
  const tagCount = activeHashtags.length;

  const handleCopy = () => {
    navigator.clipboard.writeText(hashtagsString);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%', paddingBottom: '60px' }}>
      <SEO 
        title="Free Viral Hashtag Generator - Instagram & TikTok" 
        description="Generate trending, niche, and high-engagement hashtags for Instagram, TikTok, LinkedIn, and YouTube. Boost your organic social reach."
        keywords="hashtag generator, instagram hashtags, tiktok hashtags, trending hashtags, viral hashtag tool, social media tags"
      />
      
      {/* Breadcrumbs Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px', fontSize: '0.9rem' }}>
        <Link to="/" style={{ color: 'var(--text-secondary)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Hashtag Generator</span>
      </div>

      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 8px 0' }}>
          Viral Hashtag Generator & Caption Builder
        </h1>
        <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', margin: 0 }}>
          Generate optimized hashtag tiers to maximize post discoverability across Instagram Explore, TikTok FYP, and LinkedIn feed algorithms.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '32px' }} className="tool-grid-split">
        
        {/* Left Side: Inputs & Tag Cloud */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Hash size={18} style={{ color: '#0284c7' }} /> Enter Post Niche / Keyword
            </h3>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', background: 'var(--bg-secondary)', padding: '4px', borderRadius: '12px' }}>
              {['instagram', 'tiktok', 'linkedin', 'youtube'].map(p => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPlatform(p)}
                  style={{
                    flex: 1,
                    padding: '8px',
                    borderRadius: '8px',
                    border: 'none',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    textTransform: 'capitalize',
                    cursor: 'pointer',
                    background: platform === p ? '#0284c7' : 'transparent',
                    color: platform === p ? '#ffffff' : 'var(--text-secondary)',
                    transition: 'var(--transition-smooth)'
                  }}
                >
                  {p}
                </button>
              ))}
            </div>

            <form onSubmit={generateHashtags} style={{ display: 'flex', gap: '12px' }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <Search size={18} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
                <input 
                  type="text" 
                  value={topic} 
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="e.g., travel vlog, tech gadget, healthy food" 
                  style={{ width: '100%', padding: '14px 16px 14px 44px', borderRadius: '12px', border: '1px solid var(--border-color)', fontSize: '1rem', fontWeight: 600, outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
                />
              </div>

              <button 
                type="submit" 
                style={{ background: '#0284c7', color: '#ffffff', border: 'none', padding: '0 24px', borderRadius: '12px', fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', transition: 'var(--transition-smooth)' }}
              >
                Generate
              </button>
            </form>

            <div style={{ display: 'flex', gap: '8px', marginTop: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)' }}>Try Popular:</span>
              {['digital marketing', 'travel vlog', 'tech gadget', 'healthy food'].map(preset => (
                <button 
                  key={preset}
                  type="button" 
                  onClick={() => { setTopic(preset); setTimeout(() => setActiveHashtags(hashtagPool[preset]), 50); }}
                  style={{ background: 'rgba(15, 23, 42, 0.04)', border: 'none', padding: '4px 10px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-primary)', cursor: 'pointer' }}
                >
                  {preset}
                </button>
              ))}
            </div>
          </div>

          <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>
                Hashtags Pool ({tagCount})
              </h3>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Click to include / exclude</span>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {activeHashtags.map((tag, i) => (
                <button 
                  key={i}
                  type="button" 
                  onClick={() => toggleHashtag(tag)}
                  style={{ 
                    background: 'rgba(2, 132, 199, 0.08)', 
                    color: '#0284c7', 
                    border: '1px solid rgba(2, 132, 199, 0.2)', 
                    padding: '8px 14px', 
                    borderRadius: '10px', 
                    fontSize: '0.85rem', 
                    fontWeight: 600, 
                    cursor: 'pointer', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '6px',
                    transition: 'all 0.2s'
                  }}
                >
                  <Hash size={12} /> {tag.replace('#', '')}
                </button>
              ))}
            </div>
          </div>

        </div>

        {/* Right Side: Output Box & Copy */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <TrendingUp size={18} style={{ color: '#0284c7' }} /> Optimized Hashtags Block
                </h3>
                <div style={{ fontSize: '0.8rem', color: tagCount > 30 && platform === 'instagram' ? '#ef4444' : '#10b981', fontWeight: 700 }}>
                  Active Tags: {tagCount} {platform === 'instagram' ? '(Max 30 allowed)' : ''}
                </div>
              </div>

              <AdToast message={toast} />
              <button 
                onClick={withAd('hashtag-copy', handleCopy)}
                style={{ background: copied ? '#10b981' : '#0284c7', color: '#ffffff', border: 'none', padding: '10px 20px', borderRadius: '12px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', transition: 'var(--transition-smooth)' }}
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
                {copied ? 'Copied Tags!' : 'Copy Selected Tags'}
              </button>
            </div>

            <textarea 
              rows="6"
              readOnly 
              value={hashtagsString}
              style={{ width: '100%', padding: '16px', borderRadius: '14px', border: '1px solid var(--border-color)', fontSize: '0.9rem', lineHeight: 1.6, background: 'var(--bg-secondary)', color: 'var(--text-primary)', boxSizing: 'border-box', outline: 'none', resize: 'vertical' }}
            />
          </div>

          <div style={{ background: 'linear-gradient(135deg, rgba(2, 132, 199, 0.05) 0%, rgba(14, 165, 233, 0.05) 100%)', border: '1px solid rgba(2, 132, 199, 0.2)', borderRadius: '20px', padding: '24px', display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <div style={{ background: '#0284c7', color: '#ffffff', padding: '10px', borderRadius: '12px' }}>
              <CheckCircle2 size={24} />
            </div>
            <div>
              <h4 style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 6px 0' }}>Engagement Best Practice</h4>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
                For Instagram captions, paste these hashtags either at the very bottom of your text separated by line breaks, or in the very first comment of your post within 60 seconds of publishing.
              </p>
            </div>
          </div>

        </div>

      </div>

      <style>{`
        @media (min-width: 992px) {
          .tool-grid-split { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>

      <AdUnit />
      <ToolPageExtras toolId="hashtag-generator" />
    </div>
  );
};

export default HashtagGenerator;
